import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, Form, Button, Row, Col, Spinner, Badge } from "react-bootstrap";
import Layout from "../../layout/Layout";

import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
} from "chart.js";

import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";
import "hammerjs";

import {
  getAnlikHizlar,
  getGrafikVerileri,
} from "../../../api/uretim/scadaApi";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

const MACHINE_META = [
  {
    key: "Y1",
    title: "1. Boru Makinası",
    speedName: "Y1BM (Makine 1)",
    color: "#0d6efd",
    bgColor: "rgba(13, 110, 253, 0.10)",
  },
  {
    key: "Y2",
    title: "2. Boru Makinası",
    speedName: "Y2BM (Makine 2)",
    color: "#198754",
    bgColor: "rgba(25, 135, 84, 0.10)",
  },
  {
    key: "Y3",
    title: "3. Boru Makinası",
    speedName: "Y3BM (Makine 3)",
    color: "#ffc107",
    bgColor: "rgba(255, 193, 7, 0.12)",
  },
  {
    key: "Y4",
    title: "4. Boru Makinası",
    speedName: "Y4BM (Makine 4)",
    color: "#dc3545",
    bgColor: "rgba(220, 53, 69, 0.10)",
  },
];

function toDateTimeLocalValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function getDefaultDateRange() {
  const now = new Date();

  const start = new Date(now);
  start.setHours(8, 30, 0, 0);

  const end = new Date(now);
  end.setHours(18, 0, 0, 0);

  return {
    start: toDateTimeLocalValue(start),
    end: toDateTimeLocalValue(end),
  };
}

function normalizeApiArray(response) {
  const payload = response?.data || response;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
}

function normalizeDate(value) {
  if (!value) return new Date();

  return new Date(String(value).replace("Z", ""));
}

function getSpeedValue(row, key) {
  const value = Number(row?.[key] || 0);

  if (value > 1000) return 0;

  return value;
}

function checkIsBreak(mins) {
  return (
    (mins >= 600 && mins < 610) ||
    (mins >= 720 && mins < 750) ||
    (mins >= 870 && mins < 880) ||
    (mins >= 240 && mins < 270)
  );
}

function calculateMachineStats(rows, key) {
  let calisma = 0;
  let durus = 0;
  let mola = 0;
  let hizToplami = 0;
  let calismaSayisi = 0;

  rows.forEach((row) => {
    const date = normalizeDate(row.DateTime);
    const speed = getSpeedValue(row, key);
    const mins = date.getHours() * 60 + date.getMinutes();

    if (speed > 0) {
      calisma += 10 / 60;
      hizToplami += speed;
      calismaSayisi += 1;
    } else if (checkIsBreak(mins)) {
      mola += 10 / 60;
    } else {
      durus += 10 / 60;
    }
  });

  return {
    calisma: Math.round(calisma),
    durus: Math.round(durus),
    mola: Math.round(mola),
    ortHiz: calismaSayisi > 0 ? Math.round(hizToplami / calismaSayisi) : 0,
  };
}

function buildMainDatasets(rows) {
  return MACHINE_META.map((machine) => ({
    label: machine.title,
    data: rows.map((row) => ({
      x: normalizeDate(row.DateTime),
      y: getSpeedValue(row, machine.key),
    })),
    borderColor: machine.color,
    backgroundColor: machine.color,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 5,
    fill: false,
    stepped: true,
    tension: 0,
  }));
}

function getChartOptions({ showLegend = true, startDate, endDate } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    normalized: true,
    parsing: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: showLegend,
        position: "top",
        labels: {
          boxWidth: 32,
          boxHeight: 10,
          usePointStyle: false,
        },
      },

      // Global datalabels plugin varsa bütün grafiği yazıya boğuyordu; bunu kapatıyoruz.
      datalabels: {
        display: false,
      },

      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        displayColors: true,
        callbacks: {
          title: (items) => {
            const item = items?.[0];

            if (!item) return "";

            return new Date(item.parsed.x).toLocaleString("tr-TR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });
          },
          label: (context) => {
            const label = context.dataset.label || "";
            return `${label}: ${context.parsed.y} M/DK`;
          },
        },
      },

      zoom: {
        pan: {
          enabled: true,
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          drag: {
            enabled: true,
            backgroundColor: "rgba(13, 110, 253, 0.10)",
            borderColor: "rgba(13, 110, 253, 0.35)",
            borderWidth: 1,
          },
          mode: "x",
        },
        limits: {
          y: {
            min: 0,
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        min: startDate || undefined,
        max: endDate || undefined,
        time: {
          unit: "minute",
          displayFormats: {
            minute: "HH:mm",
            hour: "HH:mm",
          },
          tooltipFormat: "dd.MM.yyyy HH:mm:ss",
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 18,
          maxRotation: 45,
          minRotation: 0,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.08)",
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 120,
        ticks: {
          maxTicksLimit: 8,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.08)",
        },
      },
    },
  };
}

function SpeedCard({ machine, index }) {
  const meta = MACHINE_META[index];
  const speed = Number(machine?.speed || 0);
  const isWorking = speed > 0;

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div className="text-muted small fw-bold">{meta.title}</div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              {machine?.name || meta.speedName}
            </div>
          </div>

          <Badge bg={isWorking ? "success" : "danger"}>
            {isWorking ? "Çalışıyor" : "Duruş"}
          </Badge>
        </div>

        <div className="d-flex align-items-end gap-2">
          <div
            className="fw-bold"
            style={{
              fontSize: 48,
              lineHeight: 1,
              color: isWorking ? "#198754" : "#dc3545",
            }}
          >
            {speed}
          </div>

          <div className="text-muted fw-bold mb-1">M/DK</div>
        </div>

        <div
          className="border-top mt-3 pt-2 text-muted"
          style={{ fontSize: 12 }}
        >
          Son veri:{" "}
          {machine?.time
            ? new Date(machine.time).toLocaleTimeString("tr-TR")
            : "--:--:--"}
        </div>
      </Card.Body>
    </Card>
  );
}

function MachineSummaryCard({ rows, machine, canvasRef }) {
  const stats = calculateMachineStats(rows, machine.key);

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <div className="fw-bold" style={{ color: machine.color }}>
          {machine.title}
        </div>

        <div className="small text-muted">
          Ort. Hız: <span className="fw-bold">{stats.ortHiz}</span>
        </div>
      </Card.Header>

      <Card.Body>
        <Row className="g-2 mb-3">
          <Col xs={4}>
            <div className="border rounded p-2 text-center bg-light">
              <div className="small text-muted">Çalışma</div>
              <div className="fw-bold text-success">{stats.calisma} dk</div>
            </div>
          </Col>

          <Col xs={4}>
            <div className="border rounded p-2 text-center bg-light">
              <div className="small text-muted">Duruş</div>
              <div className="fw-bold text-danger">{stats.durus} dk</div>
            </div>
          </Col>

          <Col xs={4}>
            <div className="border rounded p-2 text-center bg-light">
              <div className="small text-muted">Mola</div>
              <div className="fw-bold text-warning">{stats.mola} dk</div>
            </div>
          </Col>
        </Row>

        <div style={{ height: 220 }}>
          <canvas ref={canvasRef} />
        </div>
      </Card.Body>
    </Card>
  );
}

function ScadaDashboard() {
  const defaultDates = useMemo(() => getDefaultDateRange(), []);

  const [speeds, setSpeeds] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [loading, setLoading] = useState(false);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const startDateRef = useRef(defaultDates.start);
  const endDateRef = useRef(defaultDates.end);

  const mainChartCanvasRef = useRef(null);
  const mainChartRef = useRef(null);

  const machineCanvasRefs = useRef([null, null, null, null]);
  const machineChartRefs = useRef([null, null, null, null]);

  const safeSpeeds = useMemo(() => {
    return MACHINE_META.map((meta, index) => {
      return (
        speeds[index] || {
          name: meta.speedName,
          speed: 0,
          time: new Date(),
        }
      );
    });
  }, [speeds]);

  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);

  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);

  const fetchCards = useCallback(async () => {
    try {
      setCardsLoading(true);

      const response = await getAnlikHizlar();
      const data = normalizeApiArray(response);

      setSpeeds(data);
      setLastUpdatedAt(new Date());
    } catch (error) {
      console.error("getAnlikHizlar error:", error);
    } finally {
      setCardsLoading(false);
    }
  }, []);

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getGrafikVerileri({
        start: startDateRef.current,
        end: endDateRef.current,
      });

      const data = normalizeApiArray(response);

      setChartRows(data);
    } catch (error) {
      console.error("getGrafikVerileri error:", error);
      alert("Grafik verileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }, []);

  const resetAllZooms = useCallback(() => {
    if (mainChartRef.current) {
      mainChartRef.current.resetZoom();
    }

    machineChartRefs.current.forEach((chart) => {
      if (chart) chart.resetZoom();
    });
  }, []);

  useEffect(() => {
    fetchCards();
    fetchChartData();

    const interval = setInterval(() => {
      fetchCards();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchCards, fetchChartData]);

  useEffect(() => {
    if (!mainChartCanvasRef.current) return;

    const datasets = buildMainDatasets(chartRows);

    if (mainChartRef.current) {
      mainChartRef.current.data.datasets = datasets;
      mainChartRef.current.options.scales.x.min = startDate;
      mainChartRef.current.options.scales.x.max = endDate;
      mainChartRef.current.update("none");
    } else {
      mainChartRef.current = new Chart(mainChartCanvasRef.current, {
        type: "line",
        data: {
          datasets,
        },
        options: getChartOptions({
          showLegend: true,
          startDate,
          endDate,
        }),
      });
    }

    MACHINE_META.forEach((machine, index) => {
      const canvas = machineCanvasRefs.current[index];

      if (!canvas) return;

      const dataset = {
        label: machine.title,
        data: chartRows.map((row) => ({
          x: normalizeDate(row.DateTime),
          y: getSpeedValue(row, machine.key),
        })),
        borderColor: machine.color,
        backgroundColor: machine.bgColor,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
        stepped: true,
        tension: 0,
      };

      if (machineChartRefs.current[index]) {
        machineChartRefs.current[index].data.datasets = [dataset];
        machineChartRefs.current[index].options.scales.x.min = startDate;
        machineChartRefs.current[index].options.scales.x.max = endDate;
        machineChartRefs.current[index].update("none");
      } else {
        machineChartRefs.current[index] = new Chart(canvas, {
          type: "line",
          data: {
            datasets: [dataset],
          },
          options: getChartOptions({
            showLegend: false,
            startDate,
            endDate,
          }),
        });
      }
    });
  }, [chartRows, startDate, endDate]);

  useEffect(() => {
    const machineChartList = machineChartRefs.current;

    return () => {
      if (mainChartRef.current) {
        mainChartRef.current.destroy();
        mainChartRef.current = null;
      }

      machineChartList.forEach((chart) => {
        if (chart) chart.destroy();
      });

      machineChartList[0] = null;
      machineChartList[1] = null;
      machineChartList[2] = null;
      machineChartList[3] = null;
    };
  }, []);

  return (
    <Layout>
      <div className="container-fluid">
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0 fw-bold">Canlı Üretim ve Duruş Analizi</h4>
            <div className="text-muted small">
              Göktaş Metal hat performans izleme ekranı
            </div>
          </Col>

          <Col xs="auto" className="text-end">
            {lastUpdatedAt && (
              <div className="text-muted small">
                Son güncelleme: {lastUpdatedAt.toLocaleTimeString("tr-TR")}
              </div>
            )}

            {cardsLoading && (
              <div className="small text-primary">
                <Spinner size="sm" animation="border" className="me-1" />
                Güncelleniyor
              </div>
            )}
          </Col>
        </Row>

        <Row className="g-3 mb-4">
          {safeSpeeds.map((machine, index) => (
            <Col xs={12} md={6} xl={3} key={`speed-card-${index}`}>
              <SpeedCard machine={machine} index={index} />
            </Col>
          ))}
        </Row>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="bg-white">
            <Row className="g-2 align-items-end">
              <Col xs={12} lg>
                <div className="fw-bold">Genel Hat Grafiği</div>
                <div className="text-muted small">
                  Seçili tarih aralığına göre kümülatif hız grafiği
                </div>
              </Col>

              <Col xs={12} md={4} lg={3}>
                <Form.Label className="small mb-1">Başlangıç</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </Col>

              <Col xs={12} md={4} lg={3}>
                <Form.Label className="small mb-1">Bitiş</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </Col>

              <Col xs={12} md="auto">
                <Button
                  variant="primary"
                  onClick={fetchChartData}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Yükleniyor
                    </>
                  ) : (
                    "Sorgula"
                  )}
                </Button>
              </Col>

              <Col xs={12} md="auto">
                <Button
                  variant="secondary"
                  onClick={resetAllZooms}
                  disabled={loading}
                >
                  Zoom Sıfırla
                </Button>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body>
            <div className="text-muted small mb-2">
              Yakınlaştırma: mouse tekerleği / trackpad pinch / grafik üstünde
              sürükleyerek seçim. Kaydırma: yakınlaştırdıktan sonra grafiği
              sağa-sola sürükle.
            </div>

            <div style={{ height: 420 }}>
              <canvas ref={mainChartCanvasRef} />
            </div>
          </Card.Body>
        </Card>

        <Row className="g-3">
          {MACHINE_META.map((machine, index) => (
            <Col xs={12} xl={6} key={machine.key}>
              <MachineSummaryCard
                rows={chartRows}
                machine={machine}
                canvasRef={(element) => {
                  machineCanvasRefs.current[index] = element;
                }}
              />
            </Col>
          ))}
        </Row>
      </div>
    </Layout>
  );
}

export default ScadaDashboard;
